import React, { useId, useState, useCallback, MouseEvent, useEffect } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { Icon } from '@iconify/react';
import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputLabel,
  Stack,
  Typography,
} from '@mui/material';
import i18next from 'i18next';
import { useLocation } from 'react-router-dom';
import { borderTop, minWidth } from '@mui/system';
import { ReactComponent as GlobeIcon } from '../../assets/icons/globe.svg';
import Badge from '../../components/Badge/Badge';
import { ReactComponent as Logo } from '../../assets/images/logo.svg';
import NavSlidein from './NavSlideIn';
import NotificationMenu from './NotificationMenu';
import SearchBar from './Search';
import ProfileMenu from './ProfileMenu';
import LocalizationMenu from './LocalizationMenu';
import { ILanguage } from '../../types/language';
import { getLanguages } from '../../services/language';
import { useAppDispatch, useAppSelector } from '../../hooks/useRedux';
import TextField from '../../components/TextField/TextField';
import { notifications } from '../../services/vehicles';
import { setNotificationCount } from '../../redux/slices/notificationSlice';
import ProxyLoginDialog from './ProxyLoginDialog';

const Header = () => {
  // use hooks
  const dispatch = useAppDispatch();
  const location = useLocation();

  // local states
  const [notificationMenuAnchorEl, setNotificationMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [profileMenuAnchorEl, setProfileMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [localizationMenuAnchorEl, setLocalizationMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [isSideNavOpen, setIsSideNavOpen] = useState<boolean>(false);
  const [language, setLanguage] = useState('en');
  const [languages, setLanguages] = useState<ILanguage[]>([]);
  const [isProxyDialogOpen, setIsProxyDialogOpen] = React.useState(false);

  // global states
  const { count } = useAppSelector((state) => state.notification);
  const { userData } = useAppSelector((state) => state?.user);

  // constants
  const notificationMenuId = useId();
  const profileMenuId = useId();
  const localizationMenuId = useId();
  // constants
  const oktaStorage = window?.localStorage?.getItem?.('okta-token-storage');
  const userInfo = oktaStorage ? JSON.parse(oktaStorage)?.idToken?.claims : null;

  const handleClickOpen = () => {
    setIsProxyDialogOpen(true);
  };

  const handleProxyDialogClose = () => {
    setIsProxyDialogOpen(false);
  };

  // handling menu open - -
  const handleNotificationMenuOpen = useCallback((event: MouseEvent<HTMLElement>) => {
    setNotificationMenuAnchorEl(event.currentTarget);
  }, []);

  const handleProfileMenuOpen = useCallback((event: MouseEvent<HTMLElement>) => {
    setProfileMenuAnchorEl(event.currentTarget);
  }, []);

  const handleLocalizationMenuOpen = useCallback((event: MouseEvent<HTMLElement>) => {
    setLocalizationMenuAnchorEl(event.currentTarget);
  }, []);

  // handling menu close - -
  const handleMenuClose = useCallback(() => {
    setNotificationMenuAnchorEl(null);
  }, []);

  const handleProfileMenuClose = useCallback(() => {
    setProfileMenuAnchorEl(null);
  }, []);

  const handleLocalizationMenuClose = useCallback(() => {
    setLocalizationMenuAnchorEl(null);
  }, []);

  // fetching notification count
  const getNotificationCount = useCallback(async () => {
    await notifications({
      method: 'GET',
      url: 'notificationCount',
      params: {
        isActive: true,
      },
    }).then((resp) => {
      dispatch(setNotificationCount(resp?.data?.data));
    });
  }, [dispatch]);

  // handling language switch
  const handleSelectLanguage = useCallback((code: string) => {
    i18next.changeLanguage(code);
    setLanguage(code);
    const htmlElement = document.querySelector('html');
    const bodyElement = document.querySelector('body');
    if (htmlElement) {
      htmlElement.lang = code;
    }
    if (bodyElement) {
      if (code === 'ar') {
        bodyElement.dir = 'rtl';
      } else {
        bodyElement.dir = 'ltr';
      }
    }
    setLocalizationMenuAnchorEl(null);
  }, []);

  // lifecycle hooks
  useEffect(() => {
    setLanguage(i18next.language);
  }, []);

  // useEffect(() => {
  //   getLanguages().then((response) => {
  //     setLanguages(response);
  //   });
  //   getNotificationCount();
  // }, [getNotificationCount]);

  return (
    <>
      {/* <NotificationMenu id={notificationMenuId} anchorEl={notificationMenuAnchorEl} handleMenuClose={handleMenuClose} /> */}
      <ProfileMenu
        id={profileMenuId}
        anchorEl={profileMenuAnchorEl}
        handleMenuClose={handleProfileMenuClose}
        handleClickOpen={handleClickOpen}
      />
      <LocalizationMenu
        id={localizationMenuId}
        anchorEl={localizationMenuAnchorEl}
        handleMenuClose={handleLocalizationMenuClose}
        handleSelectLanguage={handleSelectLanguage}
        languages={languages}
      />
      <NavSlidein isOpen={isSideNavOpen} setIsOpen={setIsSideNavOpen} />
      <ProxyLoginDialog open={isProxyDialogOpen} onClose={handleProxyDialogClose} />
      <AppBar position="fixed" sx={{ backgroundColor: (theme) => theme.palette.grey[800], height: 57 }}>
        <Stack direction="row" height={55} alignItems="center" justifyContent="center" px={2}>
          <IconButton
            size="small"
            edge="start"
            color="inherit"
            aria-label="open drawer"
            sx={{ mr: 3 }}
            onClick={() => setIsSideNavOpen((prev) => !prev)}
          >
            <Icon icon="cil:hamburger-menu" />
          </IconButton>
          <Logo />
          <Typography
            sx={{
              paddingLeft: '8px',
              borderLeft: '1px solid #8b8b8b',
              color: '#fff',
              letterSpacing: 1,
              fontWeight: 300,
              marginLeft: '10px',
              fontSize: '12px',
              lineHeight: 1,
            }}
          >
            Vehicle 360
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          {location?.pathname === '/v360-app/' ? null : <SearchBar />}
          <Box sx={{ flexGrow: 1 }} />
          <Stack
            direction="row"
            gap={1}
            divider={<Divider orientation="vertical" flexItem />}
            color={(theme) => theme.palette.grey[500]}
            alignItems="center"
          >
            <Box sx={{ width: '285px', display: 'flex', flexWrap: 'wrap', justifyContent: 'end' }}>
              {/* - - notification icon - - - - */}
              {/* <Box onClick={handleNotificationMenuOpen} sx={{ cursor: 'pointer' }}>
              <Badge badgeContent={count} color="primary">
                <Icon icon="cil:bell" />
              </Badge>
            </Box> */}

              {/* <Stack
              direction="row"
              alignItems="center"
              justifyContent="centr"
              gap={0.25}
              sx={{ cursor: 'pointer', fontSize: 12 }}
              onClick={handleLocalizationMenuOpen}
            >
              <GlobeIcon />
              <Typography fontSize={10} textTransform="uppercase">
                {language}
              </Typography>
              <Icon icon="bi:caret-down-fill" />
            </Stack> */}
              <Avatar
                sx={{
                  width: 30,
                  height: 30,
                  fontSize: 12,
                  color: (theme) => theme.palette.grey[700],
                  // cursor: 'pointer',
                }}
                aria-label="Notifications of current user"
                aria-controls={profileMenuId}
                aria-haspopup="true"
                onClick={handleProfileMenuOpen}
              >
                {userInfo?.name?.split(' ')?.[0]?.[0]?.toUpperCase?.()}
                {userInfo?.name?.split(' ')?.[1]?.[0]?.toUpperCase?.()}
                {/* {userData?.firstName?.[0]}
                {userData?.lastName?.[0]} */}
              </Avatar>
            </Box>
          </Stack>
        </Stack>
      </AppBar>
      <Box sx={{ height: 57 }} />
    </>
  );
};

export default Header;
